import * as React from "react";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BuildIcon from "@mui/icons-material/Build";
import { useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { apiClient, axiosInstance } from "../services/apiClient";
import { UserRole } from "../generated/api-client";
import { UserToolsDialog } from "../components/admin/UserToolsDialog";

const BRAND = "#00A19C";
const BRAND_DARK = "#008C87";

const ROLE_LABELS: Record<string, string> = {
  baseUser: "Utente Base",
  mantainer: "Mantainer",
  admin: "Amministratore",
};

interface UserRow {
  id: string;
  firstname?: string | null;
  lastname?: string | null;
  email: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
}

export const UsersPage: React.FC = () => {
  const { inviteUser, user } = useAuth();
  const queryClient = useQueryClient();

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.BaseUser);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [toolsDialogUser, setToolsDialogUser] = useState<{ id: string; name: string; role: string } | null>(null);

  const hasPermission =
    user?.role === "admin" || user?.role === "mantainer";

  const { data: users = [], isLoading } = useQuery<UserRow[]>(
    "users",
    async () => {
      const result = await apiClient.list_all_users_admin_users_get();
      return (result as any[]).map((u) => ({
        id: u.id,
        firstname: u.firstname,
        lastname: u.lastname,
        email: u.email,
        role: u.role,
        is_active: u.is_active,
        is_verified: u.is_verified,
      }));
    },
    { enabled: hasPermission },
  );

  const updateUser = async (id: string, patch: Record<string, unknown>) => {
    try {
      await axiosInstance.put(`/admin/users/${id}`, patch);
      queryClient.invalidateQueries("users");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  const handleRoleChange = (id: string) => async (e: SelectChangeEvent) => {
    await updateUser(id, { role: e.target.value });
  };

  const handleActiveToggle = (id: string, current: boolean) => async () => {
    await updateUser(id, { is_active: !current });
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await apiClient.delete_user_admin_users__user_id__delete(id);
      queryClient.invalidateQueries("users");
      toast.success("Utente eliminato");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Inserisci un'email");
      return;
    }
    setInviteLoading(true);
    try {
      await inviteUser(inviteEmail.trim(), inviteRole);
      toast.success(`Invito inviato a ${inviteEmail}`);
      setInviteDialogOpen(false);
      setInviteEmail("");
      setInviteRole(UserRole.BaseUser);
      queryClient.invalidateQueries("users");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio dell'invito");
    } finally {
      setInviteLoading(false);
    }
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Non hai i permessi per accedere a questa sezione.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PeopleOutlineIcon sx={{ fontSize: 26, color: "text.primary" }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Gestione Utenti
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Gestisci gli utenti della piattaforma, modifica i ruoli e invita nuovi collaboratori.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteDialogOpen(true)}
          sx={{
            bgcolor: BRAND,
            "&:hover": { bgcolor: BRAND_DARK },
            textTransform: "none",
            fontWeight: 600,
            whiteSpace: "nowrap",
            boxShadow: "none",
          }}
        >
          Invita Utente
        </Button>
      </Box>

      {/* Users table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "#E5E7EB",
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: "#F9FAFB",
                "& th": {
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.25,
                },
              }}
            >
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Ruolo</TableCell>
              <TableCell align="center">Attivo</TableCell>
              <TableCell align="center">Verificato</TableCell>
              <TableCell align="center">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, borderBottom: 0 }}>
                  <CircularProgress size={28} sx={{ color: BRAND }} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && users.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{ py: 6, borderBottom: 0, color: "text.secondary" }}
                >
                  <Stack alignItems="center" spacing={0.5}>
                    <PeopleOutlineIcon sx={{ fontSize: 36, color: "#D1D5DB" }} />
                    <Typography variant="body2">Nessun utente trovato</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              users.map((u) => {
                const isSelf = u.id === user?.id;
                const fullName = [u.firstname, u.lastname].filter(Boolean).join(" ") || "—";
                return (
                  <TableRow
                    key={u.id}
                    hover
                    sx={{
                      "& td": {
                        borderBottom: "1px solid #F3F4F6",
                        py: 1.25,
                      },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} noWrap title={fullName}>
                        {fullName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
                      {u.email}
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={u.role}
                        onChange={handleRoleChange(u.id)}
                        disabled={isSelf}
                        sx={{
                          fontSize: "0.82rem",
                          minWidth: 160,
                          bgcolor: "#fff",
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                        }}
                      >
                        <MenuItem value="baseUser">{ROLE_LABELS.baseUser}</MenuItem>
                        <MenuItem value="mantainer">{ROLE_LABELS.mantainer}</MenuItem>
                        <MenuItem value="admin">{ROLE_LABELS.admin}</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={u.is_active}
                        onChange={handleActiveToggle(u.id, u.is_active)}
                        disabled={isSelf}
                        size="small"
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: BRAND,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={u.is_verified ? "Sì" : "No"}
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          bgcolor: u.is_verified ? "#D1FAE5" : "#F3F4F6",
                          color: u.is_verified ? "#047857" : "#6B7280",
                          border: u.is_verified ? "1px solid #04785725" : "1px solid #6B728025",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                        <Tooltip title="Gestisci accessi tool">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setToolsDialogUser({
                                id: u.id,
                                name: fullName !== "—" ? fullName : u.email,
                                role: u.role,
                              })
                            }
                            sx={{ color: "text.secondary" }}
                          >
                            <BuildIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isSelf ? "Non puoi eliminare te stesso" : "Elimina"}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={isSelf || deletingId === u.id}
                              onClick={() => setConfirmDeleteId(u.id)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: "#DC2626", bgcolor: "rgba(220,38,38,0.08)" },
                              }}
                            >
                              {deletingId === u.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirm delete dialog */}
      <Dialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Sei sicuro di voler eliminare questo utente? L'operazione è irreversibile.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 2 }}>
          <Button onClick={() => setConfirmDeleteId(null)} sx={{ textTransform: "none" }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!!deletingId}
            onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}
            sx={{ textTransform: "none", boxShadow: "none" }}
          >
            {deletingId ? <CircularProgress size={16} /> : "Elimina"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Invita Nuovo Utente</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="nuovo.utente@example.com"
              fullWidth
              required
              size="small"
            />
            <TextField
              select
              label="Ruolo"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as UserRole)}
              fullWidth
              required
              size="small"
            >
              <MenuItem value={UserRole.BaseUser}>{ROLE_LABELS.baseUser}</MenuItem>
              <MenuItem value={UserRole.Mantainer}>{ROLE_LABELS.mantainer}</MenuItem>
              <MenuItem value={UserRole.Admin}>{ROLE_LABELS.admin}</MenuItem>
            </TextField>
            <Typography variant="body2" color="text.secondary">
              L'utente riceverà un'email con un link per completare la registrazione.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 2 }}>
          <Button
            onClick={() => setInviteDialogOpen(false)}
            disabled={inviteLoading}
            sx={{ textTransform: "none" }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleInviteUser}
            variant="contained"
            disabled={inviteLoading || !inviteEmail.trim()}
            sx={{
              ml: 1,
              bgcolor: BRAND,
              "&:hover": { bgcolor: BRAND_DARK },
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
            }}
          >
            {inviteLoading ? <CircularProgress size={20} /> : "Invia Invito"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* UserToolsDialog */}
      {toolsDialogUser && (
        <UserToolsDialog
          open={Boolean(toolsDialogUser)}
          onClose={() => setToolsDialogUser(null)}
          userId={toolsDialogUser.id}
          userName={toolsDialogUser.name}
          userRole={toolsDialogUser.role}
        />
      )}
    </Box>
  );
};
